import { ComponentType } from "react";
import clsx from "clsx";
import BondDetailFields from "@/components/widgets/tradeFromScratch/bond/BondDetailFields";
import BondOptionDetailFields from "@/components/widgets/tradeFromScratch/bondOption/BondOptionDetailFields";
import FutureDetailFields from "@/components/widgets/tradeFromScratch/future/FutureDetailFields";
import { ITicketFieldsProps } from "@/components/widgets/tradeFromScratch/helper/ticketSharedHelper";
import RepoDetailFields from "@/components/widgets/tradeFromScratch/repo/RepoDetailFields";
import SwapDetailFields from "@/components/widgets/tradeFromScratch/swap/SwapDetailFields";
import SwaptionDetailFields from "@/components/widgets/tradeFromScratch/swaption/SwaptionDetailFields";
import { returnDraftLegSize } from "@/components/widgets/tradeFromScratch/helper/draftLegAdapter";
import AllocationSplits from "@/components/widgets/tradeFromScratch/ticketFields/AllocationSplits";
import { returnRoundedNoteText } from "@/components/widgets/tradeFromScratch/ticketFields/NominalInput";
import { defaultColumnWidths } from "@/lib/interfaces and Enums/ENUMColumnWidths";
import draftLegsStore from "@/store/draftLegsStore";
import { TicketInstrument } from "@/store/tradeFromScratchStore";
import {
  DraftLegDiscardButton,
  DraftLegSaveButton,
  returnMissingFieldsLabel,
  useDraftLegRoundedFrom,
} from "./helper/cellRenderers/DraftLeg/DraftLegCells";
import {
  IDraftLegRow,
  returnDraftLegBlockingReasons,
  useDraftLegAllocationsPending,
  useDraftLegHighlightedKeys,
  useDraftLegTicket,
} from "./helper/cellRenderers/DraftLeg/useDraftLegTicket";

// LQC-2941 / LQC-2943 - the detail row under a draft leg: its allocation, and the
// Security Details panel, opened by the toggle in the left gutter or the right-click
// menu. The panel is the same DetailFields component the Leg Creation widget renders.

// The grid's checkbox + expand column plus the cell padding before the Direction
// dropdown (measured), so the detail labels start on the dropdown's left edge.
const DIRECTION_CELL_INSET_PX = 5;
export const DRAFT_DETAIL_GUTTER_PX =
  defaultColumnWidths.checkbox +
  defaultColumnWidths.expandArrow +
  DIRECTION_CELL_INSET_PX;

const SecurityDetailsToggle = ({
  draftId,
  isOpen,
}: {
  draftId: string;
  isOpen: boolean;
}) => {
  const toggleSecurityDetails = draftLegsStore(
    (state) => state.toggleSecurityDetails,
  );
  return (
    <button
      data-testid={`DraftLegSecurityDetailsToggle-${draftId}`}
      id={`DraftLegSecurityDetailsToggle-${draftId}`}
      type="button"
      role="switch"
      aria-checked={isOpen}
      title={isOpen ? "Hide Security Details" : "Show Security Details"}
      onClick={() => toggleSecurityDetails(draftId)}
      className="flex flex-col items-center gap-0.5"
    >
      <span
        className={clsx(
          "flex h-4 w-7 items-center rounded-full p-0.5 transition-colors",
          isOpen
            ? "justify-end bg-gray-600"
            : "justify-start bg-gray-400 dark:bg-gray-600",
        )}
      >
        <span className="h-3 w-3 rounded-full bg-white" />
      </span>
      <span className="text-center text-[10px] leading-tight text-gray-600 dark:text-gray-300">
        Security
        <br />
        Details
      </span>
    </button>
  );
};

export const DRAFT_LEG_DETAIL_FIELDS: Record<
  TicketInstrument,
  ComponentType<ITicketFieldsProps>
> = {
  [TicketInstrument.Bond]: BondDetailFields,
  [TicketInstrument.Future]: FutureDetailFields,
  [TicketInstrument.BondOption]: BondOptionDetailFields,
  [TicketInstrument.Swap]: SwapDetailFields,
  [TicketInstrument.Swaption]: SwaptionDetailFields,
  [TicketInstrument.Repo]: RepoDetailFields,
};

const DraftLegDetail = ({ data }: { data: IDraftLegRow }) => {
  const { ticket, adapter, update } = useDraftLegTicket(data);
  const isSecurityDetailsOpen = draftLegsStore((state) =>
    state.openSecurityDetailIds.includes(data.id),
  );
  const isHoveringSave = draftLegsStore(
    (state) => state.hoveredSaveDraftId === data.id,
  );
  // Red rows while a blocked Save is hovered, as on the Leg Creation ticket.
  const highlightedKeys = useDraftLegHighlightedKeys(data);
  const allocationsPending = useDraftLegAllocationsPending(data);
  const roundedFrom = useDraftLegRoundedFrom(data);
  if (!ticket) return null;
  const DetailFields = DRAFT_LEG_DETAIL_FIELDS[data.instrument];
  const blockingReasons = returnDraftLegBlockingReasons(data, ticket, adapter);
  // An edit can't see the saved terms (maturity, legs, strike ...), so it shows none.
  const hasSecurityDetails = !data.edit?.termsLockedKeys.length;

  // w-full: the detail row is a flex parent, so without it the panel (and the portfolio
  // picker) shrinks to its content until something is selected.
  return (
    <div
      data-testid={`DraftLegDetailContainer-${data.id}`}
      id={`DraftLegDetailContainer-${data.id}`}
      className="flex w-full py-2 dark:text-white"
    >
      {/* pt: note slot (h-4) + gap-2 + 2px, so the switch centres on the h-5 Save. */}
      <div
        className="flex shrink-0 justify-center pt-[26px]"
        style={{ width: DRAFT_DETAIL_GUTTER_PX }}
      >
        {hasSecurityDetails && (
          <SecurityDetailsToggle
            draftId={data.id}
            isOpen={isSecurityDetailsOpen}
          />
        )}
      </div>
      <div className="flex w-full min-w-0 max-w-3xl flex-col gap-2 pr-6">
        {/* Fixed one-line slot, reserved when empty so the rows below never shift. */}
        <div
          data-testid={`DraftLegRoundedNote-${data.id}`}
          id={`DraftLegRoundedNote-${data.id}`}
          className="h-4 truncate px-1 text-xs leading-4 text-amber-600 dark:text-amber-400"
        >
          {roundedFrom !== null && returnRoundedNoteText(roundedFrom, "Nominal")}
        </div>
        {/* Also on the row (spec F3); here it stays in view at any widget width. */}
        <div
          data-testid={`DraftLegDetailActions-${data.id}`}
          id={`DraftLegDetailActions-${data.id}`}
          className="flex items-center gap-2"
        >
          <DraftLegSaveButton data={data} testIdPrefix="DraftLegDetail" />
          <DraftLegDiscardButton data={data} testIdPrefix="DraftLegDetail" />
          {blockingReasons.length > 0 && (
            <span
              data-testid={`DraftLegDetailMissing-${data.id}`}
              id={`DraftLegDetailMissing-${data.id}`}
              // Red while a blocked Save is hovered, as the Leg Creation footer does.
              className={clsx(
                "text-xs transition-colors",
                isHoveringSave
                  ? "font-semibold text-red-600 dark:text-red-400"
                  : "text-gray-500 dark:text-gray-400",
              )}
            >
              {returnMissingFieldsLabel(blockingReasons)}
            </span>
          )}
        </div>
        {/* Allocation 1 is this portfolio and takes what the splits leave (LEG_CREATION_PLAN Q19). */}
        <AllocationSplits
          testIdPrefix={`DraftLeg-${data.id}-`}
          portfolioCode={ticket.portfolioCode}
          onPortfolioChange={(portfolioCode) => update({ portfolioCode })}
          total={returnDraftLegSize(ticket, adapter)}
          splits={ticket.additionalAllocations}
          sizeKind={
            adapter.sizeKey === "contractCount" ? "contracts" : "nominal"
          }
          onChange={(additionalAllocations) =>
            update({ additionalAllocations })
          }
          highlightedKeys={highlightedKeys}
          pending={allocationsPending}
        />

        {hasSecurityDetails && isSecurityDetailsOpen && (
          <div
            data-testid={`DraftLegSecurityDetails-${data.id}`}
            id={`DraftLegSecurityDetails-${data.id}`}
            className="flex flex-col gap-2 pt-2"
          >
            <div className="px-1 text-xs font-semibold">Security Details</div>
            <DetailFields
              ticketId={data.id}
              highlightedKeys={highlightedKeys}
              showSecurityIdentity
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftLegDetail;
